import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import VideoCard from '@/components/feature/VideoCard';
import { categories, featuredVideos, hotVideos, newVideos, banners } from '@/mocks/videos';

function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % banners.length);
    }, 5000);
  }, []);

  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [startTimer]);

  const banner = banners[current];

  return (
    <section className="relative h-[500px] md:h-[620px] w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={banner.bgImage}
          alt={banner.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
      </div>

      <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 md:px-6 text-center">
        <div className="max-w-[800px] w-full">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-3 leading-tight">
            {banner.title}
          </h2>
          <p className="text-sm md:text-base text-background-50/80 mb-8">
            {banner.subtitle}
          </p>

          <div className="relative max-w-[560px] mx-auto mb-10">
            <div className="flex items-center bg-background-50 rounded-full overflow-hidden shadow-lg">
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                <i className="ri-search-line text-foreground-400 text-lg"></i>
              </div>
              <input
                type="text"
                placeholder="搜索电影、电视剧、综艺、动漫..."
                className="flex-1 bg-transparent border-none outline-none text-sm text-foreground-800 py-3 placeholder:text-foreground-400"
              />
              <button className="bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 text-sm font-medium rounded-full transition-colors whitespace-nowrap">
                搜索
              </button>
            </div>
          </div>

          <Link
            to={`/article/${banner.videoId}`}
            className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-full text-sm font-medium transition-all hover:scale-105 whitespace-nowrap"
          >
            <i className="ri-play-circle-fill text-lg"></i>
            立即观看
          </Link>
        </div>

        <div className="absolute bottom-8 flex items-center gap-2">
          {banners.map((_, idx) => (
            <button
              key={idx}
              onClick={() => {
                setCurrent(idx);
                startTimer();
              }}
              className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
                idx === current
                  ? 'bg-primary-500 w-6'
                  : 'bg-background-50/50 hover:bg-background-50/80'
              }`}
              aria-label={`幻灯片 ${idx + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryNav() {
  return (
    <section className="px-4 md:px-6 py-10 md:py-14 bg-background-50">
      <div className="max-w-[1200px] mx-auto">
        <div className="text-center mb-8">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
            影视分类
          </h2>
          <p className="text-sm text-foreground-500 mt-2">
            按类型浏览，快速发现你喜欢的影视内容
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className="group flex flex-col items-center p-6 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer"
            >
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-accent-100 text-accent-600 group-hover:bg-primary-200 group-hover:text-primary-600 transition-colors mb-4">
                <i className={`${cat.icon} text-2xl md:text-3xl`}></i>
              </div>
              <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors">
                {cat.name}
              </span>
              <span className="text-xs text-foreground-500 mt-1 text-center line-clamp-2">
                {cat.description.slice(0, 30)}...
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedSection() {
  return (
    <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              精选推荐
            </h2>
            <p className="text-sm text-foreground-500 mt-1">
              编辑精挑细选，不容错过的优质内容
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {featuredVideos.map((vid) => (
            <VideoCard key={vid} videoId={vid} size="normal" />
          ))}
        </div>
      </div>
    </section>
  );
}

function HotSection() {
  return (
    <section className="px-4 md:px-6 py-10 md:py-14 bg-background-50">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              热门排行
            </h2>
            <p className="text-sm text-foreground-500 mt-1">
              全网热播，大家都在看
            </p>
          </div>
          <Link
            to="/category/dianying"
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 whitespace-nowrap"
          >
            查看更多 <i className="ri-arrow-right-s-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {hotVideos.map((vid, idx) => (
            <div key={vid} className="relative">
              <div className="absolute top-2 left-2 z-10 w-7 h-7 flex items-center justify-center rounded-lg bg-foreground-900/80 text-background-50 text-xs font-bold">
                {idx + 1}
              </div>
              <VideoCard videoId={vid} size="normal" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NewSection() {
  return (
    <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              最新更新
            </h2>
            <p className="text-sm text-foreground-500 mt-1">
              每日更新，追剧不迷路
            </p>
          </div>
          <Link
            to="/category/dianshiju"
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 whitespace-nowrap"
          >
            查看更多 <i className="ri-arrow-right-s-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {newVideos.map((vid) => (
            <VideoCard key={vid} videoId={vid} size="normal" />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  return (
    <section className="px-4 md:px-6 py-12 md:py-16 bg-primary-500">
      <div className="max-w-[1200px] mx-auto">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          <div>
            <div className="text-3xl md:text-4xl font-bold text-background-50 mb-2">5000万+</div>
            <div className="text-sm text-background-50/80">月活跃用户</div>
          </div>
          <div>
            <div className="text-3xl md:text-4xl font-bold text-background-50 mb-2">10万+</div>
            <div className="text-sm text-background-50/80">影视资源</div>
          </div>
          <div>
            <div className="text-3xl md:text-4xl font-bold text-background-50 mb-2">99.7%</div>
            <div className="text-sm text-background-50/80">用户满意度</div>
          </div>
          <div>
            <div className="text-3xl md:text-4xl font-bold text-background-50 mb-2">30+</div>
            <div className="text-sm text-background-50/80">合作平台</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function NewsletterSection() {
  return (
    <section className="px-4 md:px-6 py-12 md:py-16 bg-background-50">
      <div className="max-w-[600px] mx-auto text-center">
        <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-accent-100 text-accent-600 mb-5">
          <i className="ri-mail-send-line text-2xl"></i>
        </div>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
          订阅更新通知
        </h2>
        <p className="text-sm text-foreground-500 mb-6">
          第一时间获取最新影视资源上架通知，精彩不错过
        </p>
        <form
          data-readdy-form
          id="newsletter-form"
          action="https://readdy.ai/api/form/d9n3lfgbdkpk99j7gg00"
          method="POST"
          encType="application/x-www-form-urlencoded"
        >
          <div className="flex flex-col sm:flex-row gap-3 max-w-[420px] mx-auto">
            <input
              type="email"
              name="email"
              placeholder="请输入您的邮箱地址"
              required
              className="flex-1 px-5 py-3 rounded-full border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
            />
            <button
              type="submit"
              className="bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-full text-sm font-medium transition-colors whitespace-nowrap"
            >
              立即订阅
            </button>
          </div>
          <div className="spam-guard">
            <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
          </div>
        </form>
      </div>
    </section>
  );
}

export default function Home() {
  const homeSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '神马视频',
        url: 'https://www.shenmashipin.com',
        description: '神马视频✨提供西瓜视频免费、西瓜视频等知名平台免费观影，菠萝视频、青青草视频最新电影电视剧高清全集不缓冲在线播放，畅享影视。',
        sameAs: [
          'https://weibo.com/shenmashipin',
          'https://www.bilibili.com/',
        ],
      },
      {
        '@type': 'WebSite',
        name: '神马视频',
        url: 'https://www.shenmashipin.com',
        description: '汇聚西瓜视频免费、菠萝视频、青青草视频等平台最新电影电视剧高清全集不缓冲在线观看',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://www.shenmashipin.com/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        name: '神马视频 - 首页',
        description: '神马视频首页，提供最新电影、热播电视剧、精彩综艺、人气动漫和高分纪录片的影视导航服务',
        url: 'https://www.shenmashipin.com/',
        isPartOf: {
          '@type': 'WebSite',
          name: '神马视频',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        schema={homeSchema}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <HeroBanner />
          <CategoryNav />
          <FeaturedSection />
          <HotSection />
          <NewSection />
          <StatsSection />
          <NewsletterSection />
        </main>
        <Footer />
      </div>
    </>
  );
}